/**
 * Pestana Stock de Inventario (INV-01): los contadores que filtran, la barra de
 * filtros, la tabla y el formulario para anotar un movimiento.
 *
 * Pide la misma API que la tabla de productos (`obtenerProductos`), con sus
 * filtros de stock, categoria y orden: el inventario no es otro catalogo, es
 * el mismo mirado por su stock. Se abre con lo que menos stock tiene arriba.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  Paginado,
  ProductoAdminResumen,
  ResumenInventario,
  TipoMovimientoManual,
} from '@bandu/compartido';

import { usePeticion } from '../../hooks/use-peticion';
import {
  obtenerOpcionesFiltroProductos,
  obtenerProductos,
  obtenerResumenInventario,
} from '../../servicios/administracion.servicio';
import {
  chipsStock,
  escribirFiltros,
  estadoDe,
  leerFiltrosStock,
  ORDEN_STOCK_POR_DEFECTO,
  rangoDeEstado,
  sinFiltrosStock,
} from '../../utilidades/filtros-inventario';
import type { FiltrosStock as Filtros } from '../../utilidades/filtros-inventario';
import { nombreConVariante } from '../../utilidades/producto';
import { conPagina, rutas } from '../../utilidades/rutas';
import { Paginador } from '../catalogo/Paginador';
import { AvisoError, Cargando } from '../comunes/EstadoPeticion';
import { ContadoresInventario } from './ContadoresInventario';
import { FiltrosActivos } from './FiltrosActivos';
import { FiltrosStock } from './FiltrosStock';
import { FormularioMovimiento } from './FormularioMovimiento';
import { Modal } from './Modal';
import { TablaStock } from './TablaStock';

/** Filas por pagina, como en Productos: la foto hace alta cada fila. */
const PRODUCTOS_POR_PAGINA = 16;

interface Anotacion {
  producto: ProductoAdminResumen;
  tipo: TipoMovimientoManual;
}

export function VistaStock() {
  const [parametros, setParametros] = useSearchParams();
  const pagina = Number(parametros.get('pagina') ?? '1');
  const filtros = leerFiltrosStock(parametros);
  // La cadena es la clave estable de los filtros: el objeto se rehace en cada render.
  const consultaFiltros = escribirFiltros('stock', filtros).toString();

  // Sube con cada movimiento anotado: vuelve a pedir la tabla y los contadores.
  const [version, setVersion] = useState(0);
  const [anotando, setAnotando] = useState<Anotacion | null>(null);

  const resumen = usePeticion((senal) => obtenerResumenInventario(senal), [version]);
  const opciones = usePeticion((senal) => obtenerOpcionesFiltroProductos(senal), []);
  const productos = usePeticion(
    (senal) =>
      obtenerProductos(
        {
          ...filtros,
          orden: filtros.orden ?? ORDEN_STOCK_POR_DEFECTO,
          pagina,
          porPagina: PRODUCTOS_POR_PAGINA,
        },
        senal,
      ),
    [pagina, consultaFiltros, version],
  );

  // Lo ultimo que llego se queda en pantalla mientras se vuelve a pedir: tras
  // anotar un movimiento, la tabla no desaparece para volver a aparecer igual.
  const [vigentes, setVigentes] = useState<{
    resumen: ResumenInventario | null;
    productos: Paginado<ProductoAdminResumen> | null;
  }>({ resumen: null, productos: null });
  useEffect(() => {
    if (resumen.datos !== null) setVigentes((actual) => ({ ...actual, resumen: resumen.datos }));
  }, [resumen.datos]);
  useEffect(() => {
    if (productos.datos !== null) setVigentes((actual) => ({ ...actual, productos: productos.datos }));
  }, [productos.datos]);
  const datosResumen = resumen.datos ?? vigentes.resumen;
  const datosProductos = productos.datos ?? vigentes.productos;

  function aplicar(siguientes: Filtros): void {
    // Cambiar un filtro vuelve a la pagina 1: la pagina 3 de otro resultado no significa nada.
    setParametros(escribirFiltros('stock', siguientes));
  }

  const rutaListado =
    consultaFiltros.length > 0
      ? `${rutas.administracion.inventario()}?${consultaFiltros}`
      : rutas.administracion.inventario();

  // El umbral de "por agotarse" lo manda el servidor: sin el no se dibujan los
  // controles que dependen de el, para no filtrar con un numero distinto.
  const umbral = datosResumen?.umbralPorAgotarse ?? null;
  const chips = umbral !== null ? chipsStock(filtros, umbral) : [];
  const estadoActivo = umbral !== null ? estadoDe(filtros, umbral) : null;

  return (
    <>
      {resumen.estado === 'error' ? <AvisoError error={resumen.error} /> : null}
      {datosResumen !== null && umbral !== null ? (
        <>
          <ContadoresInventario
            resumen={datosResumen}
            activo={estadoActivo === 'rango' ? null : estadoActivo}
            onElegir={(estado) => {
              aplicar({
                ...filtros,
                ...(estado === null
                  ? { stockMin: undefined, stockMax: undefined }
                  : rangoDeEstado(estado, umbral)),
              });
            }}
          />
          <FiltrosStock
            filtros={filtros}
            umbral={umbral}
            opciones={opciones.datos}
            onCambiar={aplicar}
          />
          <FiltrosActivos
            activos={chips}
            onCambiar={aplicar}
            onLimpiar={() => {
              aplicar(sinFiltrosStock(filtros));
            }}
          />
        </>
      ) : null}

      {productos.estado === 'cargando' && datosProductos === null ? (
        <Cargando que="el stock" />
      ) : null}
      {productos.estado === 'error' ? <AvisoError error={productos.error} /> : null}

      {datosProductos !== null && umbral !== null ? (
        datosProductos.items.length === 0 ? (
          <div className="panel-vacio" role="status">
            <p>
              {chips.length > 0
                ? 'Ningun producto cumple estos filtros.'
                : 'Todavia no hay productos en el catalogo.'}
            </p>
            {chips.length > 0 ? (
              <button
                type="button"
                className="btn btn-sm btn-contorno"
                onClick={() => {
                  aplicar(sinFiltrosStock(filtros));
                }}
              >
                Limpiar filtros
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <TablaStock
              productos={datosProductos.items}
              umbral={umbral}
              volver={conPagina(rutaListado, pagina)}
              onAnotar={(producto, tipo) => {
                setAnotando({ producto, tipo });
              }}
            />
            <Paginador
              pagina={datosProductos.pagina}
              total={datosProductos.total}
              porPagina={datosProductos.porPagina}
              ruta={rutaListado}
            />
          </>
        )
      ) : null}

      <Modal
        titulo="Anotar un movimiento"
        bajada={anotando !== null ? nombreConVariante(anotando.producto) : undefined}
        abierto={anotando !== null}
        onCerrar={() => {
          setAnotando(null);
        }}
      >
        {/* Se monta solo abierto y con `key`: cada apertura arranca en blanco, en el tipo tocado. */}
        {anotando !== null ? (
          <FormularioMovimiento
            key={`${anotando.producto.id}-${anotando.tipo}`}
            producto={anotando.producto}
            tipoInicial={anotando.tipo}
            onCancelar={() => {
              setAnotando(null);
            }}
            onAnotado={() => {
              setAnotando(null);
              setVersion((actual) => actual + 1);
            }}
          />
        ) : null}
      </Modal>
    </>
  );
}
